import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  MenuItem,
  Paper,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import PageTitle from "./common/PageTitle";
import { GatewaySkuStatus, GatewayType } from "../types";
import { GATEWAY_TYPE_LABELS, GATEWAY_TYPE_OPTIONS } from "../utils/gatewayType";
import { apiService } from "../api/services/apiService";
import { resolveReturnDestination } from "../utils/navigationReturn";
import { PM_HUBS } from "../utils/pmHubRoutes";
import { GATEWAY_ENV_OPTIONS } from "../utils/gatewaySkuEnv";

interface AddGatewaySkuProps {
  tenantId: string | null;
  returnPath?: string;
  returnSearch?: string;
}

const AddGatewaySku: React.FC<AddGatewaySkuProps> = ({
  tenantId,
  returnPath,
  returnSearch,
}) => {
  const navigate = useNavigate();
  const goBack = () =>
    navigate(
      returnPath
        ? resolveReturnDestination(returnPath, returnSearch)
        : PM_HUBS.gatewaySku,
    );

  const [gatewayProductIdentifier, setGatewayProductIdentifier] = useState("");
  const [gatewayProductName, setGatewayProductName] = useState("");
  const [gatewayCommonName, setGatewayCommonName] = useState("");
  const [gatewayId, setGatewayId] = useState("");
  const [gatewayType, setGatewayType] = useState<GatewayType>(GatewayType.APIGEE_EDGE);
  const [envName, setEnvName] = useState<string>("dev");
  const [skuStatus, setSkuStatus] = useState<GatewaySkuStatus>(GatewaySkuStatus.ACTIVE);
  const [description, setDescription] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const requiredFilled =
    gatewayProductIdentifier.trim() &&
    gatewayProductName.trim() &&
    gatewayCommonName.trim() &&
    gatewayId.trim() &&
    envName;

  const handleSubmit = async () => {
    if (!requiredFilled) {
      setErrorMessage("All gateway SKU fields marked * are required.");
      return;
    }
    setSubmitting(true);
    setErrorMessage(null);
    try {
      await apiService.createGatewaySku({
        tenantId,
        gatewayProductIdentifier: gatewayProductIdentifier.trim(),
        gatewayProductName: gatewayProductName.trim(),
        gatewayCommonName: gatewayCommonName.trim(),
        gatewayId: gatewayId.trim(),
        gatewayType,
        envName,
        description: description.trim() || undefined,
        skuStatus,
      });
      setSuccessOpen(true);
      setTimeout(goBack, 800);
    } catch (err: unknown) {
      setErrorMessage(err instanceof Error ? err.message : "Failed to create gateway SKU");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 720, mx: "auto", p: 3 }}>
      <PageTitle pageTitle="Add Gateway SKU" pageDescription="Register a gateway product SKU for product subscriptions." />
      <Paper sx={{ p: 3 }}>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          Gateway identifiers *
        </Typography>
        <TextField
          fullWidth
          label="Gateway product identifier *"
          margin="normal"
          value={gatewayProductIdentifier}
          onChange={(e) => setGatewayProductIdentifier(e.target.value)}
        />
        <TextField
          fullWidth
          label="Gateway product name *"
          margin="normal"
          value={gatewayProductName}
          onChange={(e) => setGatewayProductName(e.target.value)}
        />
        <TextField
          fullWidth
          label="Gateway common name *"
          margin="normal"
          value={gatewayCommonName}
          onChange={(e) => setGatewayCommonName(e.target.value)}
        />
        <TextField
          fullWidth
          label="Gateway ID *"
          margin="normal"
          value={gatewayId}
          onChange={(e) => setGatewayId(e.target.value)}
        />
        <TextField
          fullWidth
          select
          label="Gateway type *"
          margin="normal"
          value={gatewayType}
          onChange={(e) => setGatewayType(e.target.value as GatewayType)}
        >
          {GATEWAY_TYPE_OPTIONS.map((type) => (
            <MenuItem key={type} value={type}>
              {GATEWAY_TYPE_LABELS[type]}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          fullWidth
          select
          label="Environment *"
          margin="normal"
          value={envName}
          onChange={(e) => setEnvName(e.target.value)}
        >
          {GATEWAY_ENV_OPTIONS.map((env) => (
            <MenuItem key={env} value={env}>
              {env}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          fullWidth
          select
          label="SKU status"
          margin="normal"
          value={skuStatus}
          onChange={(e) => setSkuStatus(e.target.value as GatewaySkuStatus)}
        >
          {Object.values(GatewaySkuStatus).map((status) => (
            <MenuItem key={status} value={status}>
              {status}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          fullWidth
          label="Description"
          margin="normal"
          multiline
          minRows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        {errorMessage && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errorMessage}
          </Alert>
        )}
        <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
          <Button variant="outlined" onClick={goBack} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting || !requiredFilled}>
            {submitting ? "Saving…" : "Create Gateway SKU"}
          </Button>
        </Box>
      </Paper>
      <Snackbar open={successOpen} autoHideDuration={2000}>
        <Alert severity="success">Gateway SKU created</Alert>
      </Snackbar>
    </Box>
  );
};

export default AddGatewaySku;
